import type { Payor } from '@compass/client/types/payor';
import { useQuery } from '@tanstack/react-query';

import { payorService } from '@/services/api';
import { queryKeys } from '@/services/query.config';

/**
 * Fetches all payors from the PayorService API with cursor-based pagination.
 * Uses aggressive caching (30min stale, 1hr GC) since payor data changes infrequently.
 *
 * @param enabled - Whether to enable the query (set false for non-unifiedmedbv workflows)
 */
export function usePayors(enabled: boolean = true) {
  const query = useQuery({
    queryKey: queryKeys.payors.all,
    queryFn: async (): Promise<Payor[]> => {
      const allPayors: Payor[] = [];
      let startAfterId: string | undefined;

      // Paginate through all payors
      let hasMore = true;
      while (hasMore) {
        const response = await payorService.listPayors({
          limit: 1000,
          ...(startAfterId ? { start_after_id: startAfterId } : {}),
        });

        const payors = response.payors || [];
        allPayors.push(...payors);

        // Stop if no more results
        if (!response.has_more || payors.length === 0) {
          hasMore = false;
        } else {
          // Use last payor's ID as cursor
          startAfterId = payors[payors.length - 1].id;
        }
      }

      return allPayors;
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  return {
    payors: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
